import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, firstValueFrom, of, takeUntil, timeout } from 'rxjs';
import { ApiCallsService } from '../../../shared/services/api-calls.service';
import { AgGridTableService } from '../../../shared/services/ag-grid.service';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ToastService } from '../../../shared/services/toast.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


export interface ReportPayload {
  from_date: string;
  to_date: string;
  site_ids: string;
  subtype?: string;
  loc_id?: string;
  loc_from?: string;
  loc_to?: string;
  hours?: string;
  date?: string;
  alarm_id?: string;
  search?: string;
  water_loc_id?: string;
  otherType?: string;
  billing_no?: string;
  state?: string;
  is_paginate?: string | boolean;
  isPagination?: boolean;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class ReportPaginationService {
  private isInitialLoaded = false;

  private lastSiteIds: string | null = null;

  // shared state   
  private payload!: ReportPayload;
  private reportKey!: string;
  private apiUrl: string = 'Reports';

  private currentPage = 1;
  private pageSize = 10;
  private totalCount = 0;
  private fullListData: any[] = [];
  private reportConfig: any = null;

  showTable$ = new BehaviorSubject<boolean>(false);
  totalCount$ = new BehaviorSubject<number>(0);
  payloadChanged$ = new BehaviorSubject<boolean>(false);
  private mapByUrlCache = new Map<string, any>();
  sitename: string;

  constructor(
    private api: ApiCallsService,
    private aggrid: AgGridTableService,
    private http: HttpClient,
    private toast: ToastService
  ) { }

  /** Initialize filters (first load) */
  downloadheader: string = ''

  initFilters(
    payload: ReportPayload,
    reportKey: string,
    destroy$: any,
    downloadheader: string = '',
    apiUrl: string = 'Reports',
    reportConfig: any = null,
    isPagination: boolean = true
  ) {
    this.reportConfig = reportConfig;
    const loginType = localStorage.getItem('login_Type');
    const normalizedPayload: ReportPayload = { ...payload };

    if (normalizedPayload['is_paginate'] === undefined && !isPagination) {
      normalizedPayload['is_paginate'] = 'N';
    }

    if (loginType === 'CO') {
      const coLocationId = localStorage.getItem('login_id') || '';
      if (!normalizedPayload.loc_id) {
        normalizedPayload.loc_id = coLocationId;
      }
      if (!normalizedPayload.site_ids) {
        const consumerSiteId = sessionStorage.getItem('consumerSiteId');
        if (consumerSiteId) {
          normalizedPayload.site_ids = consumerSiteId;
        }
      }
      if (normalizedPayload.subtype && !normalizedPayload.otherType) {
        normalizedPayload.otherType = normalizedPayload.subtype;
      }
    }

    const payloadChanged = this.isPayloadChanged(this.payload, normalizedPayload);

    this.payloadChanged$.next(payloadChanged);

    const siteChanged =
      this.lastSiteIds !== null &&
      this.lastSiteIds !== normalizedPayload.site_ids;

    if (siteChanged || payloadChanged) {
      this.reset();
    }
    this.downloadheader = downloadheader;
    this.lastSiteIds = normalizedPayload.site_ids;
    this.payload = { ...normalizedPayload };
    this.reportKey = reportKey;
    this.apiUrl = apiUrl;

    this.showTable$.next(true);
    this.fetchPage(destroy$);
  }

  /** Pagination handler */
  changePage(
    event: { page: number; pageSize: number },
    destroy$: any
  ) {
    this.currentPage = event.page;
    this.pageSize = event.pageSize;

    const startIndex = (this.currentPage - 1) * this.pageSize;
    if (startIndex >= this.totalCount && this.totalCount > 0) return;

    this.fetchPage(destroy$);
  }

  /** Reset everything */
  reset() {
    this.isInitialLoaded = false; // 👈 important

    this.showTable$.next(false);
    this.totalCount$.next(0);
    this.fullListData = [];
    this.currentPage = 1;
    this.totalCount = 0;
    this.aggrid.processTableData([]);
  }

  // ---------------- PRIVATE ---------------- //

  private fetchPage(destroy$: any) {
    console.log(this.payload);

    const isPaginated = this.payload['is_paginate'] !== 'N' && this.payload['is_paginate'] !== 'n' && this.payload['is_paginate'] !== false && this.payload['isPagination'] !== false;

    this.api.getReport(
      this.payload.from_date ?? '',
      this.payload.to_date ?? '',
      this.reportKey,
      this.currentPage,
      this.pageSize,
      this.payload.site_ids ?? '',
      this.payload.subtype,
      this.payload.otherType,
      this.payload.loc_id,
      this.payload.water_loc_id,
      this.payload.loc_from,
      this.payload.loc_to,
      this.payload.hours,
      this.payload.date,
      this.payload.search,
      this.payload.alarm_id,
      this.payload.billing_no,
      this.payload.state,
      isPaginated,
      this.apiUrl,
      this.payload
    )
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (data) => this.processResponse(data),
        error: (err) => {
          console.error(err);
          this.toast.showToast(err.error.Message, 'colored', 5000, 'danger')
          this.aggrid.processTableData([]);

        }
      });
  }


  private enrichHeadersFromConfig(rawHeaders: any[]): any[] {
    let headers = rawHeaders ? [...rawHeaders] : [];
    const rowSpanConfig: any = this.reportConfig?.rowspan ?? this.reportConfig?.rowSpan ?? '';

    // If reportConfig specifies extraColumns, merge or append them
    if (Array.isArray(this.reportConfig?.extraColumns) && this.reportConfig.extraColumns.length) {
      const existingKeys = new Set(headers.map((h: any) => h.key));
      for (const extraCol of this.reportConfig.extraColumns) {
        if (!existingKeys.has(extraCol.key)) {
          headers.push({ ...extraCol });
          existingKeys.add(extraCol.key);
        } else {
          headers = headers.map((h: any) => h.key === extraCol.key ? { ...h, ...extraCol } : h);
        }
      }
    }

    // If reportConfig specifies rowspan array/string/boolean, mark matching headers
    if (rowSpanConfig) {
      const rowSpanKeys = Array.isArray(rowSpanConfig)
        ? rowSpanConfig
        : (typeof rowSpanConfig === 'string' && rowSpanConfig.trim() ? [rowSpanConfig.trim()] : []);

      if (rowSpanKeys.length) {
        headers = headers.map((h: any) => {
          if (rowSpanKeys.includes(h.key) && !h.rowspan && !h.rowSpan) {
            return { ...h, rowspan: true };
          }
          return h;
        });
      }
    }

    // If reportConfig specifies table_total / tableTotal / grid_total / gridTotal
    const tableTotalConfig: any = this.reportConfig?.table_total ?? this.reportConfig?.tableTotal ?? this.reportConfig?.grid_total ?? this.reportConfig?.gridTotal ?? '';
    if (tableTotalConfig) {
      const tableTotalKeys = Array.isArray(tableTotalConfig)
        ? tableTotalConfig
        : (typeof tableTotalConfig === 'string' && tableTotalConfig.trim() && tableTotalConfig !== 'true' ? [tableTotalConfig.trim()] : []);

      if (tableTotalKeys.length) {
        headers = headers.map((h: any) => {
          if (tableTotalKeys.includes(h.key)) {
            return { ...h, table_total: true };
          }
          return h;
        });
      }
    }

    // If reportConfig specifies pdf_total / pdfTotal
    const pdfTotalConfig: any = this.reportConfig?.pdf_total ?? this.reportConfig?.pdfTotal ?? '';
    if (pdfTotalConfig) {
      const pdfTotalKeys = Array.isArray(pdfTotalConfig)
        ? pdfTotalConfig
        : (typeof pdfTotalConfig === 'string' && pdfTotalConfig.trim() && pdfTotalConfig !== 'true' ? [pdfTotalConfig.trim()] : []);

      if (pdfTotalKeys.length) {
        headers = headers.map((h: any) => {
          if (pdfTotalKeys.includes(h.key)) {
            return { ...h, pdf_total: true };
          }
          return h;
        });
      }
    }

    // If reportConfig specifies csv_total / csvTotal / export_total / exportTotal
    const csvTotalConfig: any = this.reportConfig?.csv_total ?? this.reportConfig?.csvTotal ?? this.reportConfig?.export_total ?? this.reportConfig?.exportTotal ?? '';
    if (csvTotalConfig) {
      const csvTotalKeys = Array.isArray(csvTotalConfig)
        ? csvTotalConfig
        : (typeof csvTotalConfig === 'string' && csvTotalConfig.trim() && csvTotalConfig !== 'true' ? [csvTotalConfig.trim()] : []);

      if (csvTotalKeys.length) {
        headers = headers.map((h: any) => {
          if (csvTotalKeys.includes(h.key)) {
            return { ...h, csv_total: true };
          }
          return h;
        });
      }
    }

    // If reportConfig specifies total array/string, mark matching headers
    const totalConfig: any = this.reportConfig?.total ?? this.reportConfig?.grandTotal ?? this.reportConfig?.grand_total ?? '';
    if (totalConfig) {
      const totalKeys = Array.isArray(totalConfig)
        ? totalConfig
        : (typeof totalConfig === 'string' && totalConfig.trim() && totalConfig !== 'true' ? [totalConfig.trim()] : []);

      if (totalKeys.length) {
        headers = headers.map((h: any) => {
          if (totalKeys.includes(h.key)) {
            return { ...h, total: true };
          }
          return h;
        });
      }
    }

    // If reportConfig specifies columns map (like Table-Configuration schema)
    if (this.reportConfig?.columns && typeof this.reportConfig.columns === 'object') {
      const schemaCols = this.reportConfig.columns;
      const existingKeys = new Set(headers.map((h: any) => h.key));
      headers = headers.map((h: any) => {
        const col = schemaCols[h.key];
        if (col) {
          return { ...h, ...col };
        }
        return h;
      });
      for (const k of Object.keys(schemaCols)) {
        if (!existingKeys.has(k)) {
          const col = schemaCols[k];
          headers.push({ name: col.title || k, key: k, ...col });
        }
      }
    }

    return headers;
  }

  private processResponse(data: any) {
    this.isInitialLoaded = true; // ✅ mark success

    const list = data?.Data?.list || [];
    const isPaginated = this.payload['is_paginate'] !== 'N' && this.payload['is_paginate'] !== 'n' && this.payload['is_paginate'] !== false && this.payload['isPagination'] !== false;

    if (!isPaginated) {
      this.totalCount = Number(data?.Data?.total ?? data?.Data?.count ?? data?.Data?.totalCount ?? list.length ?? 0);
      this.totalCount$.next(this.totalCount);
      this.fullListData = [...list];
    } else {
      this.totalCount = Number(data?.Data?.total ?? data?.Data?.count ?? data?.Data?.totalCount ?? 0);
      this.totalCount$.next(this.totalCount);

      const startIndex = (this.currentPage - 1) * this.pageSize;

      // Ensure full list size
      while (this.fullListData.length < this.totalCount) {
        this.fullListData.push({
          id: `dummy-${this.fullListData.length}`,
          isDummy: true
        });
      }

      // Replace page slice
      list.forEach((item: any, i: number) => {
        const index = startIndex + i;
        if (index < this.totalCount) {
          this.fullListData[index] = { ...item, isDummy: false };
        }
      });
    }

    let headers = this.enrichHeadersFromConfig(data?.Data?.header || []);
    const rowSpanConfig: any = this.reportConfig?.rowspan ?? this.reportConfig?.rowSpan ?? '';

    // Check if table totals are requested for AG Grid
    const hasTableTotals = headers.some((h: any) => {
      const isTableTrue = h.table_total === true || h.table_total === 'true' || h.tableTotal === true || h.tableTotal === 'true' || h.grid_total === true || h.grid_total === 'true' || h.gridTotal === true || h.gridTotal === 'true';
      const isTableFalse = h.table_total === false || h.table_total === 'false' || h.tableTotal === false || h.tableTotal === 'false' || h.grid_total === false || h.grid_total === 'false' || h.gridTotal === false || h.gridTotal === 'false';
      if (isTableFalse) return false;
      if (isTableTrue) return true;
      const isPdfOnly = (h.pdf_total === true || h.pdf_total === 'true' || h.pdfTotal === true || h.pdfTotal === 'true') && !isTableTrue;
      if (isPdfOnly) return false;
      return h.total === true || h.total === 'true' || h.grand_total === true || h.grandTotal === true;
    });

    this.aggrid.processTableData({
      ...data,
      Data: {
        ...data.Data,
        header: headers,
        list: [...this.fullListData]
      }
    }, rowSpanConfig, hasTableTotals);
  }
  private isPayloadChanged(
    oldPayload: ReportPayload | null,
    newPayload: ReportPayload
  ): boolean {
    if (!oldPayload) return true;

    const keys = Object.keys(newPayload) as (keyof ReportPayload)[];

    return keys.some(key => oldPayload[key] !== newPayload[key]);
  }



  downloadCSV(destroy$: any, showtrue: any[] = []) {
    if (!this.payload || !this.reportKey) {
      console.warn('CSV download aborted: payload not initialized');
      return;
    }
    console.log(showtrue);

    // If totalCount not known yet, force first fetch
    const pageSizeForCSV = this.totalCount > 0 ? this.totalCount : 10000;

    this.api.getReport(
      this.payload.from_date ?? '',
      this.payload.to_date ?? '',
      this.reportKey,
      1,                       // 🔥 always page 1
      pageSizeForCSV,          // 🔥 full count
      this.payload.site_ids ?? '',
      this.payload.subtype,
      this.payload.otherType,
      this.payload.loc_id,
      this.payload.water_loc_id,
      this.payload.loc_from,
      this.payload.loc_to,
      this.payload.hours,
      this.payload.date,
      this.payload.search,
      this.payload.alarm_id,
      this.payload.billing_no,
      this.payload.state,
      true,
      this.apiUrl,
      this.payload
    )
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (data) => this.handleCSVResponse(data, showtrue),
        error: (err) => {
          console.error('❌ CSV download failed:', err)
          this.toast.showToast('CSV download failed: ' + err.error.Message, 'colored', 5000, 'danger')
        }
      });
  }
  async applyHeaderUrlMappingsForExport(
    headers: any[],
    rows: any[],
  ): Promise<any[]> {

    if (!headers?.length || !rows?.length) {
      return rows;
    }

    const urlHeaders = headers.filter(h =>
      h?.key && h?.MapByUrl && h?.MapByKey && h?.MapByValue
    );

    if (!urlHeaders.length) {
      return rows;
    }

    let updatedRows = [...rows];

    // ✅ support single OR comma-separated sites
    const siteIds: string[] = String(this.payload?.site_ids || '')
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean);

    for (const header of urlHeaders) {

      const cacheKey =
        `${header.MapByUrl}_${siteIds.sort().join('_')}`;

      let lookup: Record<string, any> = {};

      try {

        /* ---------- CACHE ---------- */
        if (this.mapByUrlCache.has(cacheKey)) {
          lookup = this.mapByUrlCache.get(cacheKey);
        }
        else {

          // ✅ call API for EACH site
          const responses = await Promise.all(
            siteIds.map(siteId =>
              firstValueFrom(
                this.http.get<any>(
                  `${environment.apiURL}/${header.MapByUrl}`,
                  {
                    params: {
                      site_id: siteId,
                      Limit: 10000,
                      Offset: 0
                    }
                  }
                ).pipe(
                  timeout(8000),
                  catchError(() => of(null))
                )
              )
            )
          );

          // ✅ merge all lookups
          lookup = responses.reduce((acc: any, res: any) => {

            const list = res?.Data?.list ?? [];

            if (!Array.isArray(list)) return acc;

            list.forEach((item: any) => {
              const key = item[header.MapByKey];
              const value = item[header.MapByValue];

              if (key != null && value != null) {
                acc[key] = value;
                acc[String(key)] = value;
              }
            });

            return acc;
          }, {});

          this.mapByUrlCache.set(cacheKey, lookup);
        }

        /* ---------- APPLY ---------- */
        updatedRows = updatedRows.map(row => {
          const val = row[header.key];
          if (
            val !== undefined &&
            val !== null &&
            val !== '' &&
            val !== '---' &&
            val !== 'N/A'
          ) {
            const mapped = lookup[val] ?? lookup[String(val)];
            if (mapped !== undefined && mapped !== null) {
              return {
                ...row,
                [header.key]: mapped
              };
            }
          }

          return {
            ...row,
            [header.key]: 'N/A'
          };
        });

      } catch {
        updatedRows = updatedRows.map(row => ({
          ...row,
          [header.key]: 'N/A'
        }));
        continue;
      }
    }

    return updatedRows;
  }



  private filterHeadersByVisibility(headers: any[], showtrue: any[]) {
    if (!Array.isArray(showtrue) || !showtrue.length) return headers;

    const visibleFields = new Set(
      showtrue
        .filter(col => col.hide === false)
        .map(col => col.field)
    );

    return headers.filter((h: any) =>
      visibleFields.has(h.key) // h.key must match field
    );
  }
  private filterRowDataByHeaders(list: any[], headers: any[]) {
    const allowedKeys = new Set(headers.map((h: any) => h.key));

    return list.map(row => {
      const filtered: any = {};
      for (const key of allowedKeys) {
        filtered[key] = row[key];
      }
      return filtered;
    });
  }
  listSitenameMapping(list: any, showtrue: any) {
    const siteNameHeader = showtrue.find(
      (h: any) => h.field === 'site_name' && h.hide === false
    );
    const sitecodeNameHeader = showtrue.find(
      (h: any) => h.field === 'short_code' && h.hide === false
    );

    if (siteNameHeader) {
      const siteList = JSON.parse(localStorage.getItem('siteList') || '[]');
      if (Array.isArray(siteList) && siteList.length) {
        const siteMap = new Map(
          siteList.map((s: any) => [String(s.site_id), s.site_name])
        );

        list = list.map((row: any) => {
          if (!row.site_name && row.site_id) {
            return {
              ...row,
              site_name: siteMap.get(String(row.site_id)) || ''
            };
          }
          return row;
        });
      }
    }
    if (sitecodeNameHeader) {
      const siteList = JSON.parse(localStorage.getItem('siteList') || '[]');
      if (Array.isArray(siteList) && siteList.length) {
        const siteMap = new Map(
          siteList.map((s: any) => [String(s.site_id), s.short_code])
        );

        list = list.map((row: any) => {
          if (!row.short_code && row.site_id) {
            return {
              ...row,
              short_code: siteMap.get(String(row.site_id)) || ''
            };
          }
          return row;
        });
      }
    }
    return list
  }
  private appendTotalRow(headers: any[], list: any[], target: 'pdf' | 'csv' | 'table' = 'pdf'): any[] {
    const isTargetTotalCol = (h: any) => {
      if (target === 'pdf') {
        if (h.pdf_total === true || h.pdf_total === 'true' || h.pdfTotal === true || h.pdfTotal === 'true' || h.export_total === true || h.exportTotal === true) {
          return true;
        }
        if (h.pdf_total === false || h.pdf_total === 'false' || h.pdfTotal === false || h.pdfTotal === 'false' || h.export_total === false || h.exportTotal === false) {
          return false;
        }
        return h.total === true || h.total === 'true' || h.grand_total === true || h.grandTotal === true;
      } else if (target === 'csv') {
        if (h.csv_total === true || h.csv_total === 'true' || h.csvTotal === true || h.csvTotal === 'true' || h.export_total === true || h.exportTotal === true || h.pdf_total === true || h.pdf_total === 'true' || h.pdfTotal === true || h.pdfTotal === 'true') {
          return true;
        }
        if (h.csv_total === false || h.csv_total === 'false' || h.csvTotal === false || h.csvTotal === 'false' || h.export_total === false || h.exportTotal === false) {
          return false;
        }
        return h.total === true || h.total === 'true' || h.grand_total === true || h.grandTotal === true;
      } else {
        if (h.table_total === true || h.table_total === 'true' || h.tableTotal === true || h.tableTotal === 'true' || h.grid_total === true || h.grid_total === 'true' || h.gridTotal === true || h.gridTotal === 'true') {
          return true;
        }
        if (h.table_total === false || h.table_total === 'false' || h.tableTotal === false || h.tableTotal === 'false' || h.grid_total === false || h.grid_total === 'false' || h.gridTotal === false || h.gridTotal === 'false') {
          return false;
        }
        if (h.pdf_total === true || h.pdf_total === 'true' || h.pdfTotal === true || h.pdfTotal === 'true') {
          return false;
        }
        return h.total === true || h.total === 'true' || h.grand_total === true || h.grandTotal === true;
      }
    };

    const totalHeaders = headers.filter(isTargetTotalCol);
    if (!totalHeaders.length) return list;

    const totalRow: any = {};
    totalRow['_isTotal'] = true;

    const dataList = list.filter(r => !r._isTotal && !r.isDummy);
    if (!dataList.length) return list;

    let labelAssigned = false;

    headers.forEach((h) => {
      const isTotalCol = isTargetTotalCol(h);
      if (isTotalCol) {
        if (h.row_formula || h.rowFormula) {
          const formula = h.row_formula || h.rowFormula;
          totalRow[h.key] = this.aggrid.evaluateRowAggregateFormula(formula, dataList);
        } else {
          totalRow[h.key] = this.aggrid.evaluateRowAggregateFormula(`sum(${h.key})`, dataList);
        }
      } else {
        if (!labelAssigned) {
          totalRow[h.key] = 'TOTAL';
          labelAssigned = true;
        } else {
          totalRow[h.key] = '';
        }
      }
    });

    return [...dataList, totalRow];
  }
  private async handleCSVResponse(data: any, showtrue: any[] = []) {
    if (data?.Status !== 'Success') return;

    let headers = this.enrichHeadersFromConfig(data?.Data?.header || []);
    let list = data?.Data?.list || [];

    if (!headers.length || !list.length) return;

    list = this.aggrid.applyRowCalculations(headers, list) || list;
    list = this.aggrid.applyFormulaCalculations(headers, list) || list;

    list = this.aggrid.applyHeaderKeyMappings(headers, list) || [];
    if (!list.length) return;

    list = await this.applyHeaderUrlMappingsForExport(headers, list);

    headers = this.filterHeadersByVisibility(headers, showtrue);
    list = this.listSitenameMapping(list, showtrue);
    list = this.filterRowDataByHeaders(list, headers);

    if (!headers.length || !list.length) return;

    // 🔥 Append total row AFTER filtering, using visible headers
    list = this.appendTotalRow(headers, list, 'csv');

    const csv = this.convertToCSVUsingHeader(headers, list);
    this.setSiteNameFromStorage();
    const fileName = `${this.downloadheader}-${this.sitename}.csv`;
    this.triggerCSVDownload(csv, fileName);
  }

  private async handlePDFResponse(data: any, showtrue: any[] = []) {
    if (data?.Status !== 'Success') return;

    let headers = this.enrichHeadersFromConfig(data?.Data?.header || []);
    let list = data?.Data?.list || [];

    if (!headers.length || !list.length) return;

    list = this.aggrid.applyRowCalculations(headers, list) || list;
    list = this.aggrid.applyFormulaCalculations(headers, list) || list;

    list = this.aggrid.applyHeaderKeyMappings(headers, list) || [];
    if (!list.length) return;

    list = await this.applyHeaderUrlMappingsForExport(headers, list);

    headers = this.filterHeadersByVisibility(headers, showtrue);
    list = this.listSitenameMapping(list, showtrue);
    list = this.filterRowDataByHeaders(list, headers);

    if (!headers.length || !list.length) return;

    // 🔥 Append total row AFTER filtering, using visible headers
    list = this.appendTotalRow(headers, list, 'pdf');

    this.setSiteNameFromStorage();
    this.generatePDF(headers, list);
  }


  private convertToCSVUsingHeader(
    headers: Array<{ name: string; key: string }>,
    data: any[]
  ): string {

    // 🔥 Filter out siteid column from CSV output
    const validHeaders = headers.filter(h =>
      h.key?.toLowerCase() !== 'siteid'
    );

    /* --------------------------------------------------
       🔹 Build site lookup map from localStorage
       -------------------------------------------------- */
    const siteList = JSON.parse(localStorage.getItem('siteList') || '[]');

    const siteMap = new Map<string, any>();
    siteList.forEach((s: any) => {
      if (s.site_id) {
        siteMap.set(String(s.site_id), s);
      }
    });

    const isSpannedCol = (h: any) => {
      if (!h) return false;
      return !!(
        h.rowspan ||
        h.rowSpan ||
        (this.reportConfig?.rowspan && (
          this.reportConfig.rowspan === h.key ||
          (Array.isArray(this.reportConfig.rowspan) && this.reportConfig.rowspan.includes(h.key))
        ))
      );
    };

    const getColGroupKey = (h: any, rowData: any) => {
      if (!rowData || rowData._isTotal) return null;
      let spanGroupKeys: string[] = [];
      const groupByRaw = h.groupBy ?? h.group_by;
      const reportRowSpanList = Array.isArray(this.reportConfig?.rowspan)
        ? this.reportConfig.rowspan
        : (this.reportConfig?.rowspan ? [this.reportConfig.rowspan] : []);

      if (Array.isArray(groupByRaw)) {
        spanGroupKeys = groupByRaw.filter((k: any) => typeof k === 'string' && k.trim());
      } else if (typeof groupByRaw === 'string' && groupByRaw.trim() && groupByRaw !== 'true') {
        spanGroupKeys = [groupByRaw.trim()];
      } else if (Array.isArray(h.rowspan)) {
        spanGroupKeys = h.rowspan.filter((k: any) => typeof k === 'string' && k.trim());
      } else if (typeof h.rowspan === 'string' && h.rowspan.trim() && h.rowspan !== 'true') {
        spanGroupKeys = [h.rowspan.trim()];
      } else if (reportRowSpanList.length && reportRowSpanList.includes(h.key)) {
        const keyIdx = reportRowSpanList.indexOf(h.key);
        spanGroupKeys = reportRowSpanList.slice(0, keyIdx + 1);
      } else {
        spanGroupKeys = [h.key];
      }

      return spanGroupKeys.map(k => String(rowData[k] ?? '')).join('___');
    };

    // Pre-calculate group bounds for spanned columns in CSV
    interface CsvGroupBounds {
      startIndex: number;
      endIndex: number;
      middleIndex: number;
    }
    const csvSpannedBoundsMap = new Map<string, Map<number, CsvGroupBounds>>();

    validHeaders.forEach(h => {
      if (!isSpannedCol(h)) return;
      const rowMap = new Map<number, CsvGroupBounds>();
      let start = 0;
      while (start < data.length) {
        if (data[start]?._isTotal) {
          start++;
          continue;
        }
        const currentGroupKey = getColGroupKey(h, data[start]);
        let end = start + 1;
        while (end < data.length && !data[end]?._isTotal && getColGroupKey(h, data[end]) === currentGroupKey) {
          end++;
        }
        const bounds: CsvGroupBounds = {
          startIndex: start,
          endIndex: end - 1,
          middleIndex: start + Math.floor((end - 1 - start) / 2)
        };
        for (let k = start; k < end; k++) {
          rowMap.set(k, bounds);
        }
        start = end;
      }
      csvSpannedBoundsMap.set(h.key, rowMap);
    });

    // CSV header row
    const headerRow = validHeaders
      .map(h => `"${h.name}"`)
      .join(',');

    // CSV data rows
    const rows = data.map((row, rIdx) =>
      validHeaders
        .map(h => {
          let value = row[h.key];

          /* --------------------------------------------
             🔹 Fallback mapping for site_name / short_code
             -------------------------------------------- */
          if (
            (value === undefined || value === null || value === '') &&
            row.siteid &&
            (h.key === 'site_name' || h.key === 'short_code')
          ) {
            const site = siteMap.get(String(row.siteid));
            value = site?.[h.key] ?? '';
          }

          if (row._isTotal) {
            return `"${String(value ?? '').replace(/"/g, '""')}"`;
          }

          // Spanning value placement for CSV
          if (isSpannedCol(h)) {
            const bounds = csvSpannedBoundsMap.get(h.key)?.get(rIdx);
            const repeat = (h as any).repeatOnEveryPage !== false && (h as any).repeat_on_page !== false && (h as any).showOnEveryPage !== false;
            const valign = String((h as any).valign || (h as any).position || 'middle').toLowerCase();

            if (!repeat && bounds) {
              if (valign === 'bottom' || valign === 'flex-end') {
                if (rIdx !== bounds.endIndex) {
                  value = '';
                }
              } else if (valign === 'top' || valign === 'flex-start') {
                if (rIdx !== bounds.startIndex) {
                  value = '';
                }
              } else {
                if (rIdx !== bounds.middleIndex) {
                  value = '';
                }
              }
            }
          }

          if (value === undefined || value === null || value === '' || value === '---') {
            value = (isSpannedCol(h) && (h as any).repeatOnEveryPage === false) ? '' : 'N/A';
          }

          return `"${String(value ?? '').replace(/"/g, '""')}"`;
        })
        .join(',')
    );

    return [headerRow, ...rows].join('\n');
  }



  private triggerCSVDownload(csv: string, filename: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    link.click();

    URL.revokeObjectURL(url);
  }
  downloadPDF(destroy$: any, showtrue: any[] = []) {
    if (!this.payload || !this.reportKey) {
      console.warn('PDF download aborted: payload not initialized');
      return;
    }

    const pageSizeForPDF = this.totalCount > 0 ? this.totalCount : 10000;

    this.api.getReport(
      this.payload.from_date ?? '',
      this.payload.to_date ?? '',
      this.reportKey,
      1,
      pageSizeForPDF,
      this.payload.site_ids ?? '',
      this.payload.subtype,
      this.payload.otherType,
      this.payload.loc_id,
      this.payload.water_loc_id,
      this.payload.loc_from,
      this.payload.loc_to,
      this.payload.hours,
      this.payload.date,
      this.payload.search,
      this.payload.alarm_id,
      this.payload.billing_no,
      this.payload.state,
      true,
      this.apiUrl,
      this.payload
    )
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (data) => this.handlePDFResponse(data, showtrue),
        error: (err) => {
          console.error('❌ PDF download failed:', err);
          this.toast.showToast('PDF download failed: ' + err, 'colored', 5000, 'danger');
        }
      });
  }

  private generatePDF(
    headers: Array<{ name: string; key: string }>,
    data: any[]
  ) {
    const validHeaders = headers.filter(h =>
      h.key?.toLowerCase() !== 'siteid'
    );

    // Decide page size based on column count
    let pageFormat: any = 'a4';

    const columnCount = validHeaders.length;

    if (columnCount <= 10) {
      pageFormat = 'a4';
    } else if (columnCount <= 16) {
      pageFormat = 'a3';
    } else if (columnCount <= 26) {
      pageFormat = 'a2';
    } else if (columnCount <= 32) {
      pageFormat = 'a1'
    } else {
      pageFormat = 'a0'
    }

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: pageFormat
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const usableWidth = pageWidth - 20; // left + right margin (10 + 10)
    const equalColumnWidth = usableWidth / columnCount;
    const columnStyles: any = {};

    validHeaders.forEach((_, index) => {
      columnStyles[index] = { cellWidth: equalColumnWidth };
    });

    // 🔥 Dynamic Font Calculation

    // Reference:
    // 12mm column ≈ font size 7 (minimum readable)
    // 22mm column ≈ font size 10
    // 35mm+ column ≈ font size 14–15

    let dynamicFontSize = Math.round(equalColumnWidth * 0.45);

    // Page-aware clamps (important)
    const fontLimits: any = {
      a4: { min: 6, max: 11 },
      a3: { min: 7, max: 13 },
      a2: { min: 8, max: 15 },
      a1: { min: 9, max: 18 }
    };

    const limits = fontLimits[String(pageFormat).toLowerCase()] || fontLimits.a4;

    dynamicFontSize = Math.max(
      limits.min,
      Math.min(dynamicFontSize, limits.max)
    );
    /* =====================================================
       ✅ HEADER SECTION
    ===================================================== */

    const payload = this.payload || {};

    const BLACKLIST = ['bill_no', 'status', 'state', 'id', 'offset', 'limit', 'site_id', 'site_ids', 'site', 'config_id', 'tower_id', 'loc_id', 'loc_from', 'loc_to', 'loc_idfrom', 'loc_idto', 'from_location_id', 'to_location_id', 'location_id', 'other_type', 'alarm_id', 'water_loc_id', 'otherType', 'orderBy', 'per_page', 'page', 'fromdate', 'todate'];

    const runDate = new Date().toLocaleString();

    // ----- We calculate filterY first to reserve space at the top of each page -----
    let headerHeight = 24;
    
    // We'll iterate payload to find out how many filter lines there are
    Object.entries(payload).forEach(([key, value]) => {
      if (BLACKLIST.includes(key.toLowerCase())) return;
      if (!value) return;

      const lowerKey = key.toLowerCase();
      if ((lowerKey === 'selected_date' || lowerKey === 'selecteddate') && ((payload as any)['date'] || (payload as any)['Date'])) {
        return;
      }
      headerHeight += 6;
    });
    
    const filterYStart = 24;
    const topMargin = headerHeight + 5;

    /* =====================================================
       ✅ FILTER ROW (From / To etc)
    ===================================================== */

    // Filters are now drawn in didDrawPage

    /* =====================================================
       ✅ TABLE (WITH PER-PAGE ROWSPAN & COLSPAN)
    ===================================================== */

    const tableHeaders = validHeaders.map(h => h.name);

    // Identify which columns should be row-spanned and their grouping keys
    const isSpannedCol = (cIdx: number) => {
      const h: any = validHeaders[cIdx];
      if (!h) return false;
      return !!(
        h.rowspan ||
        h.rowSpan ||
        (this.reportConfig?.rowspan && (
          this.reportConfig.rowspan === h.key ||
          (Array.isArray(this.reportConfig.rowspan) && this.reportConfig.rowspan.includes(h.key))
        ))
      );
    };

    const getColGroupKey = (cIdx: number, rowData: any) => {
      if (!rowData || rowData._isTotal) return null;
      const h: any = validHeaders[cIdx];
      let spanGroupKeys: string[] = [];
      const groupByRaw = h.groupBy ?? h.group_by;
      const reportRowSpanList = Array.isArray(this.reportConfig?.rowspan)
        ? this.reportConfig.rowspan
        : (this.reportConfig?.rowspan ? [this.reportConfig.rowspan] : []);

      if (Array.isArray(groupByRaw)) {
        spanGroupKeys = groupByRaw.filter((k: any) => typeof k === 'string' && k.trim());
      } else if (typeof groupByRaw === 'string' && groupByRaw.trim() && groupByRaw !== 'true') {
        spanGroupKeys = [groupByRaw.trim()];
      } else if (Array.isArray(h.rowspan)) {
        spanGroupKeys = h.rowspan.filter((k: any) => typeof k === 'string' && k.trim());
      } else if (typeof h.rowspan === 'string' && h.rowspan.trim() && h.rowspan !== 'true') {
        spanGroupKeys = [h.rowspan.trim()];
      } else if (reportRowSpanList.length && reportRowSpanList.includes(h.key)) {
        const keyIdx = reportRowSpanList.indexOf(h.key);
        spanGroupKeys = reportRowSpanList.slice(0, keyIdx + 1);
      } else {
        spanGroupKeys = [h.key];
      }

      return spanGroupKeys.map(k => String(rowData[k] ?? '')).join('___');
    };

    // Pre-calculate group bounds for every spanned column
    interface GroupBounds {
      groupKey: string;
      startIndex: number;
      endIndex: number;
      middleIndex: number;
    }
    const columnGroupBoundsMap = new Map<number, Map<number, GroupBounds>>();

    validHeaders.forEach((_, cIdx) => {
      if (!isSpannedCol(cIdx)) return;
      const rowMap = new Map<number, GroupBounds>();
      let start = 0;
      while (start < data.length) {
        if (data[start]?._isTotal) {
          start++;
          continue;
        }
        const currentGroupKey = getColGroupKey(cIdx, data[start]);
        let end = start + 1;
        while (end < data.length && !data[end]?._isTotal && getColGroupKey(cIdx, data[end]) === currentGroupKey) {
          end++;
        }
        const bounds: GroupBounds = {
          groupKey: currentGroupKey || '',
          startIndex: start,
          endIndex: end - 1,
          middleIndex: start + Math.floor((end - 1 - start) / 2)
        };
        for (let k = start; k < end; k++) {
          rowMap.set(k, bounds);
        }
        start = end;
      }
      columnGroupBoundsMap.set(cIdx, rowMap);
    });

    const tableRows: any[] = data.map(row => {
      if (row._isTotal) {
        return validHeaders.map((h, i) => {
          if (row[h.key] !== undefined && row[h.key] !== null && row[h.key] !== '') {
            return String(row[h.key]);
          }
          return i === 0 ? 'TOTAL' : '';
        });
      }
      return validHeaders.map(h => {
        const val = row[h.key];
        return (val === undefined || val === null || val === '' || val === '---') ? 'N/A' : val;
      });
    });

    interface ActiveSpannedBlock {
      colIndex: number;
      groupKey: string;
      text: string;
      x: number;
      y: number;
      width: number;
      height: number;
      pageNumber: number;
      hasFirstRowOfGroup: boolean;
      hasLastRowOfGroup: boolean;
      hasMiddleRowOfGroup: boolean;
    }

    const activeBlocks = new Map<number, ActiveSpannedBlock>();

    const flushBlock = (block: ActiveSpannedBlock) => {
      if (!block || block.height <= 0) return;
      const h: any = validHeaders[block.colIndex];
      const repeatOnEveryPage = h?.repeatOnEveryPage !== false && h?.repeat_on_page !== false && h?.showOnEveryPage !== false;
      const valign = String(h?.valign || h?.position || 'middle').toLowerCase();

      // 1. Draw continuous spanned background & border rectangle
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.2);
      doc.rect(block.x, block.y, block.width, block.height, 'FD');

      // Draw darker & thicker bottom border on the last row of the span group
      if (block.hasLastRowOfGroup) {
        doc.setDrawColor(71, 85, 105);
        doc.setLineWidth(0.6);
        doc.line(block.x, block.y + block.height, block.x + block.width, block.y + block.height);
      }

      // 2. Check if text should be drawn on this page
      let shouldDrawText = false;
      if (repeatOnEveryPage) {
        shouldDrawText = true;
      } else {
        if (valign === 'bottom' || valign === 'flex-end') {
          shouldDrawText = block.hasLastRowOfGroup;
        } else if (valign === 'top' || valign === 'flex-start') {
          shouldDrawText = block.hasFirstRowOfGroup;
        } else {
          shouldDrawText = block.hasMiddleRowOfGroup || (block.hasFirstRowOfGroup && block.hasLastRowOfGroup);
        }
      }

      if (!shouldDrawText) return;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(dynamicFontSize);
      doc.setTextColor(0, 0, 0);

      const halign = String(h?.halign || h?.align || h?.textAlign || 'left').toLowerCase();
      let textAlign: 'left' | 'center' | 'right' = 'left';
      let textX = block.x + 2;
      if (halign === 'center' || halign === 'middle') {
        textAlign = 'center';
        textX = block.x + block.width / 2;
      } else if (halign === 'right' || halign === 'end' || halign === 'flex-end') {
        textAlign = 'right';
        textX = block.x + block.width - 2;
      } else {
        textAlign = 'left';
        textX = block.x + 2;
      }

      let textY = block.y + block.height / 2;
      let baseline: 'top' | 'middle' | 'bottom' = 'middle';

      if (valign === 'top' || valign === 'flex-start') {
        textY = block.y + 3;
        baseline = 'top';
      } else if (valign === 'bottom' || valign === 'flex-end') {
        textY = block.y + block.height - 3;
        baseline = 'bottom';
      } else {
        textY = block.y + block.height / 2;
        baseline = 'middle';
      }

      const maxTextWidth = Math.max(10, block.width - 4);
      const lines = doc.splitTextToSize(block.text, maxTextWidth);
      doc.text(lines, textX, textY, { align: textAlign, baseline: baseline });
    };

    autoTable(doc, {
      head: [tableHeaders],
      body: tableRows,
      startY: topMargin,
      margin: { left: 10, right: 10, top: topMargin },
      styles: {
        fontSize: dynamicFontSize,
        cellPadding: 2,
        valign: 'middle'
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: 0,
        lineWidth: 0.2,
        fontSize: dynamicFontSize + 1
      },
      columnStyles, // ✅ equal width columns

      theme: 'grid',
      didParseCell: (hookData) => {
        const originalRow = data[hookData.row.index];

        if (hookData.section === 'body') {
          if (originalRow?._isTotal) {
            hookData.cell.styles.fontStyle = 'bold';
            hookData.cell.styles.fillColor = [245, 245, 245];

            if (typeof hookData.cell.raw === 'string') {
              hookData.cell.text = [String(hookData.cell.raw).toUpperCase()];
            }
          } else if (isSpannedCol(hookData.column.index)) {
            hookData.cell.text = [];
          }
        }
      },
      didDrawCell: (hookData) => {
        if (hookData.section !== 'body') return;
        const cIdx = hookData.column.index;
        const rowIndex = hookData.row.index;
        const originalRow = data[rowIndex];
        const cell = hookData.cell;

        // Draw darker & thicker bottom border for last row of any active span group
        let isRowSpanEnd = false;
        columnGroupBoundsMap.forEach(rowMap => {
          const bounds = rowMap.get(rowIndex);
          if (bounds && rowIndex === bounds.endIndex) {
            isRowSpanEnd = true;
          }
        });

        if (isRowSpanEnd && !originalRow?._isTotal) {
          doc.setDrawColor(71, 85, 105);
          doc.setLineWidth(0.6);
          doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
        }

        if (!isSpannedCol(cIdx)) return;

        if (originalRow?._isTotal) {
          if (activeBlocks.has(cIdx)) {
            flushBlock(activeBlocks.get(cIdx)!);
            activeBlocks.delete(cIdx);
          }
          return;
        }

        const bounds = columnGroupBoundsMap.get(cIdx)?.get(rowIndex);
        const groupKey = bounds ? bounds.groupKey : (getColGroupKey(cIdx, originalRow) || '');
        const isFirstRowOfGroup = bounds ? (rowIndex === bounds.startIndex) : false;
        const isLastRowOfGroup = bounds ? (rowIndex === bounds.endIndex) : false;
        const isMiddleRowOfGroup = bounds ? (rowIndex === bounds.middleIndex) : false;

        const val = originalRow[validHeaders[cIdx].key];
        const text = (val === undefined || val === null || val === '' || val === '---') ? 'N/A' : String(val);
        const pageNumber = doc.getNumberOfPages();

        if (activeBlocks.has(cIdx)) {
          const currentBlock = activeBlocks.get(cIdx)!;
          if (currentBlock.groupKey === groupKey && currentBlock.pageNumber === pageNumber) {
            currentBlock.height += cell.height;
            if (isFirstRowOfGroup) currentBlock.hasFirstRowOfGroup = true;
            if (isLastRowOfGroup) currentBlock.hasLastRowOfGroup = true;
            if (isMiddleRowOfGroup) currentBlock.hasMiddleRowOfGroup = true;
          } else {
            flushBlock(currentBlock);
            activeBlocks.set(cIdx, {
              colIndex: cIdx,
              groupKey: groupKey,
              text: text,
              x: cell.x,
              y: cell.y,
              width: cell.width,
              height: cell.height,
              pageNumber: pageNumber,
              hasFirstRowOfGroup: isFirstRowOfGroup,
              hasLastRowOfGroup: isLastRowOfGroup,
              hasMiddleRowOfGroup: isMiddleRowOfGroup
            });
          }
        } else {
          activeBlocks.set(cIdx, {
            colIndex: cIdx,
            groupKey: groupKey,
            text: text,
            x: cell.x,
            y: cell.y,
            width: cell.width,
            height: cell.height,
            pageNumber: pageNumber,
            hasFirstRowOfGroup: isFirstRowOfGroup,
            hasLastRowOfGroup: isLastRowOfGroup,
            hasMiddleRowOfGroup: isMiddleRowOfGroup
          });
        }
      },
      didDrawPage: () => {
        activeBlocks.forEach(block => flushBlock(block));
        activeBlocks.clear();

        // ---- DRAW HEADER ON EACH PAGE ----
        const pageNumber = doc.getNumberOfPages();

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(`Site : ${this.sitename || '-'}`, 14, 10);

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(
          this.downloadheader || this.formatKey(this.reportKey),
          pageWidth / 2,
          12,
          { align: 'center' }
        );

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`Page ${pageNumber}`, pageWidth - 14, 10, { align: 'right' });
        doc.text(`Run Date : ${runDate}`, pageWidth - 14, 16, { align: 'right' });

        let currentFilterY = filterYStart;
        doc.setFontSize(9);

        Object.entries(payload).forEach(([key, value]) => {
          if (BLACKLIST.includes(key.toLowerCase())) return;
          if (!value) return;

          const lowerKey = key.toLowerCase();
          if ((lowerKey === 'selected_date' || lowerKey === 'selecteddate') && ((payload as any)['date'] || (payload as any)['Date'])) {
            return;
          }

          const label = this.formatKey(key);
          doc.text(`${label} : ${value}`, 14, currentFilterY);
          currentFilterY += 6;
        });

        doc.line(10, currentFilterY, pageWidth - 10, currentFilterY);
      }
    });

    doc.save(`${this.downloadheader || this.formatKey(this.reportKey)}-${this.sitename}.pdf`);
  }
  private formatKey(key: string): string {
    const customLabels: Record<string, string> = {
      unit_from: 'From Unit',
      unit_to: 'To Unit',
      from_unit: 'From Unit',
      to_unit: 'To Unit',
      unit: 'Unit',
      search: 'Search By',
      search_by: 'Search By',
      searchby: 'Search By',
      filter_search: 'Search By',
      filtersearch: 'Search By',
      search_text: 'Search By',
      searchtext: 'Search By',
      search_term: 'Search By',
      searchterm: 'Search By',
      search_val: 'Search By',
      searchval: 'Search By',
      search_value: 'Search By',
      searchvalue: 'Search By',
      search_query: 'Search By',
      searchquery: 'Search By',
      search_input: 'Search By',
      searchinput: 'Search By'
    };
    const lower = key.toLowerCase();
    if (customLabels[lower]) {
      return customLabels[lower];
    }
    if (lower === 'search' || lower.startsWith('search') || lower.endsWith('search') || lower.includes('_search') || lower.includes('search_')) {
      return 'Search By';
    }
    return key
      .toLowerCase()                 // normalize casing first
      .replace(/[_-]+/g, ' ')        // replace _ and - with space
      .replace(/\b\w/g, c => c.toUpperCase()); // capitalize words
  }

  private setSiteNameFromStorage(): void {

    let siteIdsRaw: string | null = this.payload?.site_ids || null;
    const siteListRaw = localStorage.getItem('siteList');
    if (!siteListRaw) {
      this.sitename = '';
      return;
    }

    try {
      const siteList = JSON.parse(siteListRaw);

      // ✅ fallback if empty
      if (!siteIdsRaw) {
        const encoded = localStorage.getItem('site_idLogin');
        siteIdsRaw = encoded ? atob(encoded) : null;
      }

      if (!siteIdsRaw) {
        this.sitename = '';
        return;
      }

      // ✅ normalize ids
      const siteIds = String(siteIdsRaw)
        .split(',')
        .map(id => id.trim())
        .filter(Boolean);

      // ✅ find matches
      let matchedSites = siteList.filter((site: any) =>
        siteIds.includes(String(site.site_id))
      );

      // ✅ fallback if no match found
      if (matchedSites.length === 0) {
        const encoded = localStorage.getItem('site_idLogin');
        const decoded = encoded ? atob(encoded) : null;

        if (decoded) {
          matchedSites = siteList.filter((site: any) =>
            String(site.site_id) === decoded
          );
        }
      }

      const matchedNames = matchedSites.map((site: any) => site.site_name);

      // ✅ final display
      this.sitename =
        matchedNames.length > 3
          ? `${matchedNames.slice(0, 3).join(', ')} +${matchedNames.length - 3} more`
          : matchedNames.join(', ');

    } catch (e) {
      console.error('Invalid siteList in localStorage');
      this.sitename = '';
    }
  }

}
